import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { useEffect, useRef } from "react";
import { ArrowDownRight, Play } from "lucide-react";
import { scrollToId } from "./SmoothScroll";
import { LiveDot, LiveNumber } from "./LiveNumber";
import { useT } from "../lib/i18n";
import { usePlayer } from "../context/PlayerContext";
import { SocialLinks, useSocials } from "./SocialLinks";

const EASE = [0.16, 1, 0.3, 1];
const BARS = Array.from({ length: 48 }, (_, i) => i);

const Mask = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden pt-[0.1em] -mt-[0.1em] ${className}`}>
    <motion.span className="block" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, ease: EASE, delay }}>
      {children}
    </motion.span>
  </span>
);

const Wave = () => (
  <svg viewBox="0 0 600 40" className="w-full max-w-[640px] h-8 sm:h-10 my-2" preserveAspectRatio="none" aria-hidden="true">
    <motion.path
      d="M0 20 H262 l6 -8 l6 16 l6 -18 l6 22 l6 -26 l6 30 l6 -22 l6 14 l6 -8 l6 4 H600"
      fill="none" stroke="#1FD1FF" strokeWidth="2" strokeLinejoin="round"
      style={{ filter: "drop-shadow(0 0 6px rgba(31,209,255,0.8))" }}
      initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.6, ease: EASE, delay: 0.55 }}
    />
  </svg>
);

const useBeat = (barsRef, eyeRef, glowRef) => {
  const { graphRef, playing } = usePlayer();
  useEffect(() => {
    const bars = barsRef.current?.children || [];
    const live = playing && graphRef.current?.analyser;
    for (const b of bars) b.classList.toggle("eq-bar", !live);
    if (!live) {
      for (const b of bars) b.style.transform = "";
      if (eyeRef.current) { eyeRef.current.style.opacity = ""; eyeRef.current.style.transform = ""; }
      if (glowRef.current) glowRef.current.style.opacity = "";
      return undefined;
    }
    const an = graphRef.current.analyser;
    const data = new Uint8Array(an.frequencyBinCount);
    let id;
    const tick = () => {
      an.getByteFrequencyData(data);
      const n = bars.length;
      for (let i = 0; i < n; i++) {
        const v = data[Math.floor((i / n) * data.length * 0.8)] / 255;
        bars[i].style.transform = `scaleY(${Math.max(0.06, v)})`;
      }
      const bass = (data[0] + data[1] + data[2] + data[3]) / (4 * 255);
      const kick = Math.pow(bass, 2.2);
      if (eyeRef.current) {
        eyeRef.current.style.opacity = String(0.25 + kick * 0.75);
        eyeRef.current.style.transform = `translate(-50%,-50%) scale(${0.8 + kick * 1.2})`;
      }
      if (glowRef.current) glowRef.current.style.opacity = String(0.35 + kick * 0.65);
      id = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(id);
  }, [playing, graphRef, barsRef, eyeRef, glowRef]);
};

const Equalizer = ({ barsRef }) => (
  <div ref={barsRef} data-testid="hero-equalizer" aria-hidden="true" className="absolute bottom-0 inset-x-0 h-28 flex items-end gap-[3px] px-2 opacity-50 pointer-events-none [mask-image:linear-gradient(to_right,transparent,black_30%,black)]">
    {BARS.map((i) => (
      <span key={i} className="eq-bar flex-1 h-full origin-bottom bg-gradient-to-t from-[#1FD1FF] to-[#1FD1FF]/20 transition-transform duration-75" style={{ animationDelay: `${(i * 137) % 1000}ms`, height: `${45 + ((i * 53) % 55)}%` }} />
    ))}
  </div>
);

export const HeroNeon = ({ stats, latest, onPlayLatest }) => {
  const ref = useRef(null);
  const barsRef = useRef(null);
  const eyeRef = useRef(null);
  const glowRef = useRef(null);
  useBeat(barsRef, eyeRef, glowRef);
  const socials = useSocials();
  const { t } = useT();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 100, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 100, damping: 20 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const artY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} onMouseMove={onMove} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden bg-[#04070D]" style={{ perspective: 1400 }}>
      <motion.div
        style={{ y: artY, rotateX: rx, rotateY: ry }}
        initial={{ opacity: 0, scale: 1.12 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.8, ease: EASE, delay: 0.2 }}
        className="absolute inset-y-0 right-0 w-full lg:w-[74%] max-lg:top-16 max-lg:bottom-auto max-lg:h-[55vh] [mask-image:linear-gradient(to_right,transparent,black_28%)] max-lg:[mask-image:linear-gradient(to_bottom,black_55%,transparent_100%)]"
      >
        <img src="/brand/hero.webp" alt="Arte neón de DEEJAY T-REX" className="w-full h-full object-cover object-[35%_30%] lg:object-right max-lg:opacity-70" />
        <span ref={eyeRef} data-testid="hero-eye-glow" aria-hidden="true" className="eye-glow absolute left-[39%] top-[15%] w-24 h-24 rounded-full pointer-events-none mix-blend-screen"
          style={{ background: "radial-gradient(circle, rgba(255,255,255,0.95) 0%, rgba(31,209,255,0.85) 18%, rgba(31,209,255,0.25) 45%, transparent 70%)" }} />
      </motion.div>
      <div ref={glowRef} className="absolute inset-0 pointer-events-none opacity-50 transition-opacity duration-100 bg-[radial-gradient(ellipse_at_70%_45%,rgba(31,209,255,0.28),transparent_60%)]" />
      <Equalizer barsRef={barsRef} />

      <motion.div style={{ y: textY }} className="relative z-10 max-w-[1500px] mx-auto px-4 lg:px-10 pt-32 lg:pt-40 pb-20 max-lg:pt-[42vh]">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="mb-6">
          {socials.tiktok ? (
            <a href={socials.tiktok} target="_blank" rel="noopener noreferrer" data-testid="hero-live-tiktok-link"
              className="inline-flex items-center gap-3 px-4 py-2 border border-[#1FD1FF]/50 bg-[#1FD1FF]/10 rounded-full font-mono text-xs uppercase tracking-[0.25em] text-[#1FD1FF] hover:bg-[#1FD1FF] hover:text-black transition-colors">
              <span className="w-2 h-2 rounded-full bg-current animate-pulse" /> {t("hero.live")} <span aria-hidden="true">↗</span>
            </a>
          ) : (
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#1FD1FF] flex items-center gap-3">
              <span className="w-2 h-2 rounded-full bg-[#1FD1FF] animate-pulse shadow-[0_0_10px_#1FD1FF]" /> {t("hero.kicker")}
            </p>
          )}
        </motion.div>
        <h1 aria-label="DEEJAY T-REX" className="max-w-[720px]">
          <Mask delay={0.35} className="font-wide text-white text-[9vw] sm:text-5xl lg:text-6xl xl:text-7xl tracking-[0.32em] leading-none">DEEJAY</Mask>
          <Wave />
          <Mask delay={0.5} className="font-heavy text-[20vw] sm:text-[8.5rem] lg:text-[9vw] xl:text-[9.5rem] leading-[0.95]">
            <span className="text-[#1FD1FF]" style={{ textShadow: "0 0 28px rgba(31,209,255,0.55)" }}>T-</span><span className="text-white">REX</span>
          </Mask>
        </h1>
        <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.8 }}
          data-testid="hero-tagline" className="mt-6 font-mono text-sm sm:text-lg uppercase tracking-[0.35em] text-white">
          {t("hero.tag1")} <span className="text-[#1FD1FF] font-bold">{t("hero.tagA")}</span> {t("hero.tag2")} <span className="text-[#1FD1FF] font-bold">{t("hero.tagB")}</span>
        </motion.p>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }} className="mt-6 max-w-md text-base text-[#C7D2E0] leading-relaxed">
          {t("hero.body")}
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.8 }} className="mt-9 flex flex-wrap gap-4">
          <button data-testid="hero-play-button" onClick={onPlayLatest} disabled={!latest}
            className="group flex items-stretch bg-[#1FD1FF] text-black font-mono text-sm font-bold uppercase tracking-[0.2em] rounded-sm hover:bg-white transition-colors disabled:opacity-50">
            <span className="px-6 py-4">{t("hero.play")}</span>
            <span className="px-4 grid place-items-center border-l border-black/30"><Play className="w-4 h-4 fill-current group-hover:scale-125 transition-transform" /></span>
          </button>
          <button data-testid="hero-explore-tools-button" onClick={() => scrollToId("tools")}
            className="flex items-center gap-3 px-6 py-4 border border-[#1FD1FF]/70 rounded-sm font-mono text-sm uppercase tracking-[0.2em] hover:bg-[#1FD1FF]/10 hover:border-[#1FD1FF] transition-colors">
            {t("hero.tools")} <ArrowDownRight className="w-4 h-4 text-[#1FD1FF]" />
          </button>
        </motion.div>
        <div className="mt-12"><LiveDot label={t("stat.live")} testid="hero-stats-live" /></div>
        <dl className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-y-5 gap-x-4 max-w-xl">
          {[["plays", stats?.plays], ["downloads", stats?.downloads], ["members", stats?.members], ["mixes", stats?.mixes]].map(([k, v], i) => (
            <div key={k} data-testid={`hero-stat-${k}`} className={i % 2 ? "border-l border-white/15 pl-4" : i ? "sm:border-l sm:border-white/15 sm:pl-4" : ""}>
              <dd className="font-heavy text-2xl sm:text-3xl text-white"><LiveNumber value={v} testid={`hero-stat-${k}-value`} /></dd>
              <dt className="font-mono text-[11px] uppercase tracking-[0.12em] sm:tracking-[0.2em] text-[#A8B3C4] truncate">{t(`stat.${k}`)}</dt>
            </div>
          ))}
        </dl>
        <SocialLinks />
      </motion.div>
    </section>
  );
};
